import React, { useState, useRef, useEffect } from 'react';
import { ModalWindow } from '../../../modal/ModalWindow';
import { ApiEndpoints } from '../../../../api/apiEndpoints';

export function ShopItemAutomatizationModal({ automatizationId, setAutomatizationId, fetchItems, setPage }) {
    const [taskId, setTaskId] = useState(null);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState(0);
    const [error, setError] = useState(null);
    const [isStarting, setIsStarting] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const abortRef = useRef(null);
    const inFlightRef = useRef(false);
    const modalWindowRef = useRef();
    const didRefreshRef = useRef(false);

    const resetState = () => {
      setTaskId(null);
      setProgress(0);
      setStatus(0);
      setError(null);
      setIsStarting(false);
      setIsPolling(false);
      didRefreshRef.current = false;
    };

    const close = () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      resetState();
      setAutomatizationId(null);
    };

    const safeReadJson = async (res) => {
      try {
        return await res.json();
      } catch {
        return null;
      }
    };

    const normalizeStatusPayload = (data) => {
      if (!data) return null;
      // C# обычно отдаёт PascalCase, но на всякий случай поддержим camelCase.
      const Progress = data.Progress ?? data.progress;
      const Status = data.Status ?? data.status;
      const Error = data.Error ?? data.error;
      const Creation = data.Creation ?? data.creation;
      const Finish = data.Finish ?? data.finish;
      return { Progress, Status, Error, Creation, Finish };
    };

    const startTask = async () => {
      if (taskId || isStarting) return;

      setError(null);
      setIsStarting(true);

      const ac = new AbortController();
      abortRef.current = ac;

      try {
        const res = await fetch(`${ApiEndpoints.StartShopAutomation}/${automatizationId}`, {
          method: 'POST',
          signal: ac.signal
        });

        if (!res.ok) {
          const errorData = await safeReadJson(res);
          setError(errorData?.message || `Request failed (${res.status})`);
          return;
        }

        const id = (await res.text())?.replace(/^"|"$/g, '')?.trim();
        if (!id) {
          setError('Server did not return taskId');
          return;
        }

        setTaskId(id);
        // AutomationJobStatus: Queued=0, Running=1, Completed=2, Failed=3
        setStatus(1);
      } catch (e) {
        if (e?.name !== 'AbortError') {
          setError(e?.message || 'Network error');
        }
      } finally {
        setIsStarting(false);
      }
    };

    useEffect(() => {
      resetState();
      return () => {
        if (abortRef.current) abortRef.current.abort();
      };
    }, [automatizationId]);

    useEffect(() => {
      if (!taskId) return;

      const ac = new AbortController();
      abortRef.current = ac;

      let intervalId = null;
      setIsPolling(true);

      const stopPolling = () => {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        setIsPolling(false);
      };

      const tick = async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;

        try {
          const res = await fetch(`${ApiEndpoints.AutomationsStatus}?taskId=${encodeURIComponent(taskId)}`, {
            signal: ac.signal
          });

          if (res.status === 404) {
            setError('Task not found (maybe expired). Try starting it again.');
            setStatus(3);
            stopPolling();
            return;
          }

          if (!res.ok) {
            const errorData = await safeReadJson(res);
            setError(errorData?.message || `Status request failed (${res.status})`);
            setStatus(3);
            stopPolling();
            return;
          }

          const raw = await safeReadJson(res);
          const data = normalizeStatusPayload(raw);
          if (!data) {
            setError('Failed to parse status response');
            setStatus(3);
            stopPolling();
            return;
          }

          if (typeof data.Progress === 'number') {
            const p = Math.max(0, Math.min(100, Math.trunc(data.Progress)));
            setProgress(p);
          }
          if (typeof data.Status === 'number') setStatus(data.Status);
          if (data.Error) setError(String(data.Error));

          const isTerminal = data.Status === 2 || data.Status === 3;
          if (isTerminal) {
            stopPolling();

            if (data.Status === 2 && !didRefreshRef.current) {
              didRefreshRef.current = true;
              try {
                setPage(0);
                await fetchItems(0);
              } catch {
                // ignore
              }
            }
          }
        } catch (e) {
          if (e?.name !== 'AbortError') {
            setError(e?.message || 'Network error');
            setStatus(3);
            stopPolling();
          }
        } finally {
          inFlightRef.current = false;
        }
      };

      tick();
      intervalId = setInterval(tick, 1000);

      return () => {
        if (intervalId) clearInterval(intervalId);
        ac.abort();
        setIsPolling(false);
      };
    }, [taskId, fetchItems]);

    const statusText = status === 0
      ? 'Queued'
      : status === 1
        ? 'Running'
        : status === 2
          ? 'Completed'
          : 'Failed';

    const progressBarClass = status === 2 ? 'bg-success' : status === 3 ? 'bg-danger' : 'bg-primary';

    return (
      <ModalWindow
        ref={modalWindowRef}
        title="Run automation"
        onClose={close}
      >
        <p>Run automation: {automatizationId}</p>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="mb-2 d-flex align-items-center justify-content-between">
          <div>
            <strong>Status:</strong> {statusText}
          </div>
          <div className="text-muted">
            {taskId ? <small>taskId: {taskId}</small> : <small>taskId: —</small>}
          </div>
        </div>

        <div className="progress mb-3" style={{ height: 20 }}>
          <div
            className={`progress-bar ${progressBarClass}`}
            role="progressbar"
            style={{ width: `${progress}%` }}
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {progress}%
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={close}
            disabled={isStarting || isPolling}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={startTask}
            disabled={isStarting || isPolling || taskId !== null}
          >
            {isStarting ? 'Starting…' : 'Start'}
          </button>
        </div>
      </ModalWindow>
    );
}
